import React, { useEffect, useCallback } from 'react';

/**
 * Custom hook that handles click outside logic.
 * @param {Function} outsideClickHandler - Handler to call when clicking outside
 * @param {string} outsideParent - CSS selector for the parent element
 * @param {boolean} outsideCondition - Whether to check for outside clicks
 */
export const useClickOutside = ({
	outsideClickHandler,
	outsideParent,
	outsideCondition,
}) => {
	const handleClickOutside = useCallback(
		(event) => {
			if (outsideCondition) {
				const isChild = event.target?.closest(outsideParent);
				const isClickTagInsideComponent =
					isChild &&
					(event.target?.tagName === 'BUTTON' ||
						event.target?.parentNode?.tagName === 'A');

				if (!isClickTagInsideComponent) {
					outsideClickHandler();
				}

				if (!isChild) {
					outsideClickHandler();
				}
			}
		},
		[outsideClickHandler, outsideParent, outsideCondition],
	);

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [handleClickOutside]);
};

function withClickOutside(WrappedComponent) {
	const WithClickOutside = (props) => {
		useClickOutside(props);
		return <WrappedComponent {...props} />;
	};
	WithClickOutside.displayName = `withClickOutside(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
	return WithClickOutside;
}

export default withClickOutside;
